import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  TableBody,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import BotonVolver from "../../components/kit/BotonVolver";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import EstadoChip from "../../components/kit/EstadoChip";
import EnlaceTema from "../../components/tema/EnlaceTema";
import { usePestanaUrl } from "../../utils/estadoLista";
import { Accesores, ordenar, useOrden } from "../../utils/orden";
import Etiqueta from "../../components/kit/Etiqueta";
import { useEvaluadorResumen, useUpdateEvaluador } from "../../queries/useEvaluadores";
import { useAreas } from "../../queries/useTemas";
import { useAuthStore } from "../../stores/authStore";
import { ResumenInstancia, ResumenSolicitud } from "../../types/evaluador";
import { AreaInvestigacion } from "../../types/tema";
import { useIr } from "../../utils/enlaces";
import { estiloDeInstancia } from "../../utils/estados";

/** Ficha de un docente para el coordinador: solicitudes/guía/coguía/evaluaciones y sus
 *  áreas de especialidad. Solo la ve el coordinador. */
export default function EvaluadorDetalle() {
  const { rut } = useParams();
  const { user } = useAuthStore();
  const { data, isLoading, isError } = useEvaluadorResumen(Number(rut));
  const actualizar = useUpdateEvaluador();

  if (!user?.es_coordinador_global) return <Navigate to="/app" replace />;

  const rutNum = Number(rut);
  const flag = (input: { puede_comision?: boolean; puede_crear_tema?: boolean }) => actualizar.mutate({ rut: rutNum, input });

  return (
    <Box>
      <BotonVolver fallback="/app/evaluadores" etiquetaFallback="Evaluadores" />

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudo cargar la ficha del docente.</Alert>}

      {data && (
        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Typography variant="h4">{data.nombre}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              RUT {data.rut} · {data.jerarquia_display || "Sin jerarquía"}
            </Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={
                  <Switch checked={data.puede_comision} disabled={actualizar.isPending} onChange={(e) => flag({ puede_comision: e.target.checked })} />
                }
                label="Integra comisiones"
              />
              <FormControlLabel
                control={
                  <Switch checked={data.puede_crear_tema} disabled={actualizar.isPending} onChange={(e) => flag({ puede_crear_tema: e.target.checked })} />
                }
                label="Puede publicar temas"
              />
            </Stack>

            {/* Las áreas son del mismo tipo que los switches: lo que el coordinador
                configura de esta persona. Van juntas, no al final de la ficha. */}
            <Divider sx={{ my: 2 }} />
            <AreasSeccion rut={rutNum} areas={data.areas} />
          </Paper>

          <RolesTabs solicitudes={data.solicitudes} guia={data.guia} coguia={data.coguia} evaluaciones={data.evaluaciones} />
        </Stack>
      )}
    </Box>
  );
}

/** Áreas de especialidad: chips + edición inline (Autocomplete). */
function AreasSeccion({ rut, areas }: { rut: number; areas: AreaInvestigacion[] }) {
  const { data: opciones = [] } = useAreas();
  const actualizar = useUpdateEvaluador();
  const [editando, setEditando] = useState(false);
  const [sel, setSel] = useState<AreaInvestigacion[]>(areas);

  const guardar = () => actualizar.mutate({ rut, input: { areas: sel.map((a) => a.id) } }, { onSuccess: () => setEditando(false) });

  return (
    <Seccion
      titulo="Áreas de especialidad"
      accion={
        editando ? undefined : (
          <Button size="small" onClick={() => (setSel(areas), setEditando(true))}>
            Editar
          </Button>
        )
      }
    >
      {editando ? (
        <Stack spacing={1.5}>
          <Autocomplete
            multiple
            options={opciones}
            getOptionLabel={(a) => a.nombre}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={sel}
            onChange={(_, value) => setSel(value)}
            renderInput={(params) => <TextField {...params} label="Áreas" placeholder="Buscar área" />}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" disabled={actualizar.isPending} onClick={guardar}>
              Guardar
            </Button>
            <Button size="small" disabled={actualizar.isPending} onClick={() => setEditando(false)}>
              Cancelar
            </Button>
          </Stack>
        </Stack>
      ) : areas.length ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {areas.map((a) => (
            <Etiqueta key={a.id}>{a.nombre}</Etiqueta>
          ))}
        </Stack>
      ) : (
        <Vacio texto="Sin áreas." />
      )}
    </Seccion>
  );
}

const COL_SOL: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante" },
  { label: "Tema", sortKey: "tema" },
  { label: "Mensaje", sortKey: "mensaje" },
];
const ACC_SOL: Accesores<ResumenSolicitud> = {
  estudiante: (f) => f.estudiante,
  tema: (f) => f.tema,
  mensaje: (f) => f.mensaje,
};
// Una fila por estudiante, con el último paso que hizo con este docente: por eso la
// columna dice "Último paso" y lleva su período al lado (ver DocenteResumenView).
const COL_ROL: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante" },
  { label: "Tema", sortKey: "tema" },
  { label: "Último paso", sortKey: "paso" },
  { label: "Período", width: 96, sortKey: "periodo" },
  { label: "Estado", sortKey: "estado" },
];
const COL_EVAL: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante" },
  { label: "Tema", sortKey: "tema" },
  { label: "Último paso", sortKey: "paso" },
  { label: "Período", width: 96, sortKey: "periodo" },
  { label: "Estado del proceso", sortKey: "estado" },
  { label: "Mi evaluación", sortKey: "evaluacion" },
];

const ACC_INST: Accesores<ResumenInstancia> = {
  estudiante: (f) => f.estudiante,
  tema: (f) => f.tema,
  paso: (f) => f.paso,
  periodo: (f) => f.periodo,
  estado: (f) => f.estado,
  // Lo pendiente primero al ordenar ascendente: es lo que hay que ir a hacer.
  evaluacion: (f) => (f.ya_evalue ? `1 ${f.categoria || f.nota || ""}` : "0"),
};

/**
 * Tabs Solicitudes / Guía / Coguía / Evaluaciones.
 *
 * "Solicitudes" solo existe si hay alguna: es una bandeja de pendientes, y una
 * bandeja vacía no es información que valga una pestaña —además de que abría la
 * ficha en un cartel de "no tiene nada". Guía/Coguía/Evaluaciones son roles: se
 * muestran siempre, aunque estén en cero.
 */
function RolesTabs({
  solicitudes,
  guia,
  coguia,
  evaluaciones,
}: {
  solicitudes: ResumenSolicitud[];
  guia: ResumenInstancia[];
  coguia: ResumenInstancia[];
  evaluaciones: ResumenInstancia[];
}) {
  const navigate = useNavigate();
  const abrir = (rut: number | null) => rut != null && navigate(`/app/estudiantes/${rut}`);

  const pestanas = [
    ...(solicitudes.length
      ? [{ key: "solicitudes", label: `Solicitudes (${solicitudes.length})`, panel: <TablaSolicitudes filas={solicitudes} /> }]
      : []),
    { key: "guia", label: `Guía (${guia.length})`, panel: <TablaRol filas={guia} onAbrir={abrir} vacio="No guía a ningún estudiante." /> },
    {
      key: "coguia",
      label: `Coguía (${coguia.length})`,
      panel: <TablaRol filas={coguia} onAbrir={abrir} vacio="No coguía a ningún estudiante." />,
    },
    { key: "evaluaciones", label: `Evaluaciones (${evaluaciones.length})`, panel: <TablaEvaluaciones filas={evaluaciones} onAbrir={abrir} /> },
  ];
  // En la URL, para volver a la misma pestaña desde la ficha de un estudiante. Se pasan
  // solo las que existen: si la de solicitudes no está —se resolvió la última— una URL
  // que la pida cae en la primera en vez de dejar la ficha en blanco.
  const [tab, setTab] = usePestanaUrl("ver", pestanas.map((p) => p.key));
  const actual = pestanas.find((p) => p.key === tab) ?? pestanas[0];

  return (
    <Box>
      <Tabs
        value={actual.key}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {pestanas.map((p) => (
          <Tab key={p.key} value={p.key} label={p.label} sx={{ textTransform: "none" }} />
        ))}
      </Tabs>

      {actual.panel}
    </Box>
  );
}

function TablaSolicitudes({ filas }: { filas: ResumenSolicitud[] }) {
  const ir = useIr();
  const { orden, onSort } = useOrden();
  if (!filas.length) return <Vacio texto="No tiene solicitudes pendientes." />;
  return (
    <TablaKit columnas={COL_SOL} minWidth={720} orden={orden} onSort={onSort}>
      <TableBody>
        {ordenar(filas, ACC_SOL, orden).map((f) => (
          <FilaKit key={f.id} onClick={(e) => ir(e, `/app/temas/${f.tema_id}`)}>
            <CeldaPrincipal maxWidth={200}>{f.estudiante}</CeldaPrincipal>
            <CeldaKit maxWidth={280}>{f.tema}</CeldaKit>
            <CeldaKit maxWidth={300} atenuada={!f.mensaje}>
              {f.mensaje || "—"}
            </CeldaKit>
          </FilaKit>
        ))}
      </TableBody>
    </TablaKit>
  );
}

function TablaRol({ filas, onAbrir, vacio }: { filas: ResumenInstancia[]; onAbrir: (rut: number | null) => void; vacio: string }) {
  const { orden, onSort } = useOrden();
  if (!filas.length) return <Vacio texto={vacio} />;
  return (
    <TablaKit columnas={COL_ROL} minWidth={720} orden={orden} onSort={onSort}>
      <TableBody>
        {ordenar(filas, ACC_INST, orden).map((f) => (
          <FilaKit key={f.instancia_id} onClick={() => onAbrir(f.estudiante_rut)}>
            <CeldaPrincipal maxWidth={200}>{f.estudiante}</CeldaPrincipal>
            <CeldaKit maxWidth={280} atenuada={!f.tema}>
              <EnlaceTema id={f.tema_id} titulo={f.tema} />
            </CeldaKit>
            <CeldaKit>{f.paso}</CeldaKit>
            <CeldaKit noWrap>{f.periodo}</CeldaKit>
            <CeldaKit>
              <EstadoChip label={f.estado} {...estiloDeInstancia(f.estado_codigo)} denso />
            </CeldaKit>
          </FilaKit>
        ))}
      </TableBody>
    </TablaKit>
  );
}

function TablaEvaluaciones({ filas, onAbrir }: { filas: ResumenInstancia[]; onAbrir: (rut: number | null) => void }) {
  const { orden, onSort } = useOrden();
  if (!filas.length) return <Vacio texto="No integra ninguna comisión." />;
  return (
    <TablaKit columnas={COL_EVAL} minWidth={860} orden={orden} onSort={onSort}>
      <TableBody>
        {ordenar(filas, ACC_INST, orden).map((f) => (
          <FilaKit key={f.instancia_id} onClick={() => onAbrir(f.estudiante_rut)}>
            <CeldaPrincipal maxWidth={200}>{f.estudiante}</CeldaPrincipal>
            <CeldaKit maxWidth={260} atenuada={!f.tema}>
              <EnlaceTema id={f.tema_id} titulo={f.tema} />
            </CeldaKit>
            <CeldaKit>{f.paso}</CeldaKit>
            <CeldaKit noWrap>{f.periodo}</CeldaKit>
            <CeldaKit>
              <EstadoChip label={f.estado} {...estiloDeInstancia(f.estado_codigo)} denso />
            </CeldaKit>
            <CeldaKit>
              {/* La etiqueta antes que el número: en un paso por categorías lo que dijo
                  el profesor es "Aprobado con comentarios", no el 4.0 al que equivale. */}
              {f.ya_evalue ? (
                <EstadoChip label={f.categoria || (f.nota ? `Nota ${f.nota}` : "Evaluó")} tono="exito" icono="check" denso />
              ) : (
                <EstadoChip label="Por evaluar" tono="aviso" denso />
              )}
            </CeldaKit>
          </FilaKit>
        ))}
      </TableBody>
    </TablaKit>
  );
}

function Seccion({ titulo, accion, children }: { titulo: string; accion?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6">{titulo}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {accion}
      </Stack>
      {children}
    </Box>
  );
}

function Vacio({ texto }: { texto: string }) {
  return (
    <Paper elevation={0} sx={{ p: 3, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
      <Typography color="text.secondary" variant="body2">
        {texto}
      </Typography>
    </Paper>
  );
}
